import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import { Avatar } from './Avatar';
import type { GoogleConnectionStatus, GoogleContact } from '../types';

type ListResponse = { items: GoogleContact[]; nextPageToken: string | null };
type ImportResponse = {
  imported: number;
  skipped: number;
  errors: { resourceName: string; reason: string }[];
};

/**
 * Pick from the lawyer's Google Contacts and bulk-import as Leads. Three
 * states:
 *   1. Not connected to Google → CTA to /api/auth/google/start
 *   2. Connected but lacking contacts.readonly scope → Reconnect CTA (same
 *      endpoint; new scope is bundled and re-consent will land on it)
 *   3. Ready → checkbox list with search, "Import N as leads"
 */
export function ImportGoogleContactsModal({
  onImported,
  onClose,
}: {
  onImported: (count: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['leads', 'google', 'common']);

  const [status, setStatus] = useState<GoogleConnectionStatus | null>(null);
  const [items, setItems] = useState<GoogleContact[]>([]);
  const [pageToken, setPageToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scopeMissing, setScopeMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  async function refreshStatus() {
    try {
      const s = await apiFetch<GoogleConnectionStatus>('/api/auth/google/status');
      setStatus(s);
      return s;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }

  async function loadPage(token: string | null) {
    setError(null);
    setScopeMissing(false);
    if (!token) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const url = token ? `/api/google/contacts?pageToken=${encodeURIComponent(token)}` : '/api/google/contacts';
      const res = await apiFetch<ListResponse>(url);
      setItems((prev) => (token ? [...prev, ...res.items] : res.items));
      setPageToken(res.nextPageToken);
      setHasMore(Boolean(res.nextPageToken));
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'scope_missing') setScopeMissing(true);
        else if (e.code === 'not_connected') {
          // status will reflect this; render CTA
        } else {
          setError(e.message);
        }
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    void (async () => {
      const s = await refreshStatus();
      // contactsActive = scope granted AND the user hasn't disabled the
      // Contacts integration in Settings. Don't auto-load (and 412) when off.
      if (s?.connected && s.contactsActive) {
        await loadPage(null);
      } else {
        setLoading(false);
      }
    })();
  }, []);

  async function connectGoogle() {
    try {
      const { url } = await apiFetch<{ url: string }>('/api/auth/google/start', {
        method: 'POST',
      });
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function toggle(rn: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(rn)) next.delete(rn);
      else next.add(rn);
      return next;
    });
  }

  async function selectAll() {
    setError(null);
    setLoadingAll(true);
    try {
      let acc = items;
      let token = pageToken;
      while (token) {
        const res = await apiFetch<ListResponse>(
          `/api/google/contacts?pageToken=${encodeURIComponent(token)}`,
        );
        acc = [...acc, ...res.items];
        token = res.nextPageToken;
      }
      setItems(acc);
      setPageToken(null);
      setHasMore(false);
      // Only pick contacts that aren't already existing clients —
      // those are rendered as link-rows, not checkboxes.
      setPicked(new Set(acc.filter((c) => !c.existingClientId).map((c) => c.resourceName)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingAll(false);
    }
  }

  function toggleAllVisible() {
    setPicked((prev) => {
      const next = new Set(prev);
      const candidates = filtered.filter((c) => !c.existingClientId);
      const allOn = candidates.every((c) => next.has(c.resourceName));
      if (allOn) {
        for (const c of candidates) next.delete(c.resourceName);
      } else {
        for (const c of candidates) next.add(c.resourceName);
      }
      return next;
    });
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (c) =>
        (c.name ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q) ||
        (c.phone ?? '').toLowerCase().includes(q),
    );
  }, [items, search]);

  async function importPicked() {
    if (picked.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<ImportResponse>('/api/google/contacts/import-as-leads', {
        method: 'POST',
        body: JSON.stringify({ resourceNames: Array.from(picked) }),
      });
      onImported(res.imported);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // ─── Render branches ────────────────────────────────────────────────

  const showConnectCta = status !== null && (!status.connected || scopeMissing);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">{t('leads:import.title')}</div>
            <div className="form-preview-type">{t('leads:import.subtitle')}</div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ×
          </button>
        </div>

        {showConnectCta ? (
          <div className="form-section">
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
              {scopeMissing
                ? t('leads:import.reconnect_hint')
                : t('leads:import.connect_hint')}
            </div>
            <button className="btn-primary" onClick={connectGoogle}>
              {scopeMissing
                ? t('leads:import.reconnect_button')
                : t('leads:import.connect_button')}
            </button>
          </div>
        ) : loading ? (
          <div className="form-section">
            <div className="empty">{t('leads:import.loading')}</div>
          </div>
        ) : (
          <>
            <div className="form-section">
              <input
                placeholder={t('leads:import.search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--border2)',
                  borderRadius: 8,
                }}
              />
              <div
                style={{
                  marginTop: 8,
                  fontSize: 11,
                  color: 'var(--text3)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>
                  {t('leads:import.selected', { count: picked.size })}
                </span>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    className="btn-link-light"
                    onClick={() => void selectAll()}
                    disabled={loadingAll}
                    style={{ color: 'var(--accent-mid)' }}
                  >
                    {loadingAll
                      ? t('leads:import.loading_all')
                      : t('leads:import.select_all')}
                  </button>
                  <button
                    type="button"
                    className="btn-link-light"
                    onClick={toggleAllVisible}
                    style={{ color: 'var(--accent-mid)' }}
                  >
                    {t('leads:import.toggle_all_visible')}
                  </button>
                </div>
              </div>
            </div>

            <div
              className="form-section"
              style={{ maxHeight: 360, overflowY: 'auto', padding: 0 }}
            >
              {filtered.length === 0 ? (
                <div className="empty">{t('leads:import.empty')}</div>
              ) : (
                filtered.map((c) => {
                  const display = c.name?.trim() || c.email || c.phone || '—';
                  const isOn = picked.has(c.resourceName);
                  // Already a client → render as a link, not a checkbox.
                  // Re-importing would make a duplicate that the next
                  // /api/leads round-trip would still flag as existing,
                  // and the user has explicitly asked us not to surface
                  // these as importable.
                  if (c.existingClientId) {
                    return (
                      <a
                        key={c.resourceName}
                        href={`/clients/${c.existingClientId}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 18px',
                          borderTop: '1px solid var(--border)',
                          background: 'var(--bg-soft, transparent)',
                          textDecoration: 'none',
                          color: 'inherit',
                          opacity: 0.85,
                        }}
                        onClick={(e) => {
                          // SPA navigation; avoid full page reload.
                          e.preventDefault();
                          onClose();
                          window.history.pushState(null, '', `/clients/${c.existingClientId}`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                      >
                        <Avatar name={display} size="sm" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {display}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--text3)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {[c.email, c.phone, c.organization].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            color: 'var(--accent-mid)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t('leads:import.already_client')} →
                        </span>
                      </a>
                    );
                  }
                  return (
                    <label
                      key={c.resourceName}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 18px',
                        borderTop: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: isOn ? 'var(--accent-light)' : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggle(c.resourceName)}
                      />
                      <Avatar name={display} size="sm" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {display}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text3)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {[c.email, c.phone, c.organization].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
              {hasMore && (
                <div style={{ padding: 10, textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => loadPage(pageToken)}
                    disabled={loadingMore}
                  >
                    {loadingMore
                      ? t('common:actions.loading_more')
                      : t('common:actions.load_more')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {error && (
          <div className="form-section" style={{ color: 'var(--red)' }}>
            {error}
          </div>
        )}

        {!showConnectCta && (
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={importPicked}
              disabled={busy || picked.size === 0}
            >
              {busy
                ? t('leads:import.importing')
                : t('leads:import.import_n', { count: picked.size })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
